import { encode as base64encode } from "base64-arraybuffer";
import { jwtDecode, JwtPayload as DefaultJwtPayload } from "jwt-decode";
import queryString from "query-string";
import RouteService from "src/services/routeService";

import roles, { AccessRole, EmteriaRoles } from "../data/roles";

import TimeService from "./timeService/timeService";
import { getAuthClient, getEnv, getHomepageUrl, getReduxVersion, getSettings, toAppUrl } from "./configService";
import HttpService from "./httpService";
import { LocalStorage } from "./localStorageService";

function generateRandomHexString(byteLength: number): string {
    const array = new Uint8Array(byteLength);
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest("SHA-256", data);
    const base64Digest = base64encode(digest);
    return base64Digest.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

const authHelpers = {
    generateRandomHexString,
    generateCodeChallenge,
};

type SessionToken = {
    token_type: string;
    scope: string;
    access_token: string;
    refresh_token: string;
    expires_in: number;
    expires_at?: string | null;
    refresh_token_expires_in: number;
    refresh_token_expires_at?: string | null;
};

type JwtPayload = DefaultJwtPayload & {
    email: string;
    sub: string;
    role: string | string[];
    tft?: string;
};

const clearSession = (): void => {
    LocalStorage.removeItem("token");
    LocalStorage.removeItem("totp");
};

const getSession = (): SessionToken | null => {
    try {
        const currentReduxVersion = getReduxVersion();

        if (LocalStorage.getItem("reduxVersion") !== currentReduxVersion) {
            LocalStorage.setItem("reduxVersion", currentReduxVersion);
        }

        // TODO:
        // Let's make sure the returned session is always valid.
        // For this, we could refresh the session 5 min before its actual expiration to avoid "Unauthorized" responses completely.
        /*
        if (!sessionToken || !sessionToken.expires_at) {
            console.error("Failed session validation due to missing 'expires_at' value");
            return null;
        }

        const tokenExpirationDateMs = DateTime.fromISO(sessionToken.expires_at).diffNow().toSeconds();
        if (tokenExpirationDateMs <= 60) {
            sessionToken = refreshSession(sessionToken.refresh_token);
        }
        */

        const sessionToken = LocalStorage.getItem<SessionToken>("token");
        return sessionToken;
    } catch (_error) {
        clearSession();
        return null;
    }
};

const saveSession = (token: SessionToken): void => {
    if (!token) {
        return;
    }

    if (token.expires_in && !token.expires_at) {
        token.expires_at = TimeService.getNow()
            .plus({ seconds: Number(token.expires_in) })
            .toISO();
    }

    if (token.refresh_token_expires_in && !token.refresh_token_expires_at) {
        token.refresh_token_expires_at = TimeService.getNow()
            .plus({ seconds: Number(token.refresh_token_expires_in) })
            .toISO();
    }

    LocalStorage.setItem("token", token);
};

type RequestJwtParams = {
    body: Record<string, string>;
};

const requestJwt = async ({ body }: RequestJwtParams) => {
    const endpoint = await RouteService.getTokensRoute();
    const configData = {
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
    };

    try {
        const { data: token }: { data: SessionToken } = await HttpService.post(endpoint, queryString.stringify(body), configData);
        return token;
    } catch (ex) {
        return Promise.reject(ex);
    }
};

export enum AuthGrantType {
    refresh_token = "refresh_token",
    authorization_code = "authorization_code",
}

const requestRefreshJwt = async (refreshToken: string) => {
    const { clientSecret, clientId } = getAuthClient();

    const body = {
        grant_type: AuthGrantType.refresh_token,
        refresh_token: refreshToken,
        client_id: clientId,
        client_secret: clientSecret,
    };

    const token = await requestJwt({ body });
    return token;
};

const callbackRedirectUri = `${window.location.origin}${import.meta.env.BASE_URL}authorization/callback`;

type PendingAuth = { codeVerifier: string; returnUrl: string; state: string };
const pendingAuthTtlMs = 900_000;
const pendingAuthKey = "oauthPendingAuth";
const loginNoticeStorageKey = "loginNotice";

const storePendingAuth = (payload: PendingAuth): void => {
    const envelope = { value: payload, expire: Date.now() + pendingAuthTtlMs };
    sessionStorage.setItem(pendingAuthKey, JSON.stringify(envelope));
};
const clearPendingAuth = (): void => sessionStorage.removeItem(pendingAuthKey);
const getPendingAuth = (): PendingAuth | null => {
    const raw = sessionStorage.getItem(pendingAuthKey);
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw) as { value: PendingAuth; expire: number };
        if (Date.now() > parsed.expire) {
            clearPendingAuth();
            return null;
        }
        return parsed.value;
    } catch {
        clearPendingAuth();
        return null;
    }
};

// Constant-time string comparison to avoid leaking the stored state via timing differences.
const safeStringEquals = (a: string, b: string): boolean => {
    if (a.length !== b.length) {
        return false;
    }
    let mismatch = 0;
    for (let i = 0; i < a.length; i += 1) {
        mismatch |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }
    return mismatch === 0;
};

export class LoginRequestError extends Error {
    status: number;

    constructor(status: number, message: string) {
        super(message);
        this.name = "LoginRequestError";
        this.status = status;
    }
}

// The keys checked (message/ErrorMessage/error_description) match the ones `unpackError`
// already understands, so callers get the same message they would for axios-based errors.
const parseHttpErrorMessage = async (res: Response): Promise<string> => {
    const contentType = res.headers.get("content-type") ?? "";
    let message = `HTTP ${res.status}`;
    try {
        if (contentType.includes("application/json")) {
            const data = (await res.json()) as Record<string, unknown>;
            const candidate = data?.message ?? data?.ErrorMessage ?? data?.error_description;
            if (typeof candidate === "string" && candidate.length > 0) {
                message = candidate;
            }
        } else {
            const text = (await res.text()).replace(/^"|"$/g, "");
            if (text.length > 0) {
                message = text;
            }
        }
    } catch {
        // body unreadable — keep default `HTTP <status>` message
    }
    return message;
};

const throwHttpError = async (res: Response): Promise<never> => {
    throw new LoginRequestError(res.status, await parseHttpErrorMessage(res));
};

const initiateAuthFlow = async (returnUrl: string = "/dashboard"): Promise<void> => {
    const { clientId } = getAuthClient();
    const codeVerifier = generateRandomHexString(28);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateRandomHexString(32);
    storePendingAuth({ codeVerifier, returnUrl, state });
    const endpoint = await RouteService.getTokensRoute();
    const params = queryString.stringify({
        response_type: "code",
        client_id: clientId,
        redirect_uri: callbackRedirectUri,
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        state,
    });
    window.location.href = endpoint + "authorization?" + params;
};

const continueAuthFlowAfterLogin = async (): Promise<void> => {
    const params = queryString.parse(window.location.search, { decode: true });
    if (params.client_id) {
        const endpoint = await RouteService.getTokensRoute();
        window.location.href = endpoint + "authorization?" + queryString.stringify(params);
        return;
    }
    await initiateAuthFlow();
};

const exchangePkceAuthorizationCode = async (code: string, pending: PendingAuth): Promise<string> => {
    const { clientId, clientSecret } = getAuthClient();
    const endpoint = await RouteService.getTokensRoute();
    const body = queryString.stringify({
        grant_type: AuthGrantType.authorization_code,
        client_id: clientId,
        client_secret: clientSecret,
        code,
        code_verifier: pending.codeVerifier,
        redirect_uri: callbackRedirectUri,
    });
    const res = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
    });
    if (!res.ok) {
        await throwHttpError(res);
    }
    const token = (await res.json()) as SessionToken;
    saveSession(token);
    // Reaching the authorization_code grant means the IdP already accepted the user's password
    // and, if 2FA was required, cleared it. Mark the local TOTP flag as valid unconditionally
    // so the React app skips its own 2FA prompt.
    LocalStorage.setItem("totp", true);
    clearPendingAuth();
    return pending.returnUrl;
};

// Origins we are willing to redirect to after login. The current origin is always trusted;
// the homepage and discussion forum come from tenant config so each tenant only trusts its own
// satellite sites (e.g. the emteria forum). Malformed config values are ignored rather than fatal.
const getTrustedOrigins = (): string[] => {
    const origins = [window.location.origin];
    try {
        const homepage = getHomepageUrl();
        if (homepage) {
            origins.push(new URL(homepage).origin);
        }
    } catch {
        // ignore malformed homepageUrl
    }
    try {
        const forum = getSettings().features.helpLinks?.discussionForum;
        if (forum) {
            origins.push(new URL(forum).origin);
        }
    } catch {
        // ignore malformed forum URL
    }
    return origins;
};

type SafeReturnUrl = { kind: "internal"; url: string } | { kind: "external"; url: string };

// Validate a post-login returnUrl against an allow-list to prevent open redirects.
// Internal app paths are always allowed; absolute URLs are only allowed when their origin
// is trusted (see getTrustedOrigins). Anything else returns null so callers fall back to the dashboard.
const resolveSafeReturnUrl = (returnUrl: string): SafeReturnUrl | null => {
    if (!returnUrl) {
        return null;
    }
    // Internal: path-relative, but not protocol-relative ("//evil.com") or backslash tricks ("/\evil.com").
    if (returnUrl.startsWith("/") && !returnUrl.startsWith("//") && !returnUrl.startsWith("/\\")) {
        return { kind: "internal", url: returnUrl };
    }
    try {
        const parsed = new URL(returnUrl);
        if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
            return null;
        }
        if (getTrustedOrigins().includes(parsed.origin)) {
            return { kind: "external", url: parsed.toString() };
        }
    } catch {
        // not a parseable absolute URL
    }
    return null;
};

type OauthCallbackResult =
    | { ok: true; redirectTo: string; external: boolean }
    | { ok: false; reason: "idp_error" | "missing_code" | "invalid_state" | "exchange_failed"; message?: string };

const handleAuthCallback = async (search: string): Promise<OauthCallbackResult> => {
    const params = queryString.parse(search, { decode: true });
    const { code, state, error, error_description: errorDescription } = params;

    if (typeof error === "string" && error.length > 0) {
        clearPendingAuth();
        const message = typeof errorDescription === "string" && errorDescription.length > 0 ? errorDescription : error;
        return { ok: false, reason: "idp_error", message };
    }

    if (typeof code !== "string") {
        clearPendingAuth();
        return { ok: false, reason: "missing_code" };
    }
    const pending = getPendingAuth();
    if (!pending || typeof state !== "string" || !safeStringEquals(pending.state, state)) {
        clearPendingAuth();
        return { ok: false, reason: "invalid_state" };
    }
    try {
        const returnUrl = await exchangePkceAuthorizationCode(code, pending);
        const safe = resolveSafeReturnUrl(returnUrl);
        const redirectTo = safe ? safe.url : "/dashboard";
        return { ok: true, redirectTo, external: safe?.kind === "external" };
    } catch {
        clearPendingAuth();
        return { ok: false, reason: "exchange_failed" };
    }
};

interface BuildAuthCodeEndpoint {
    codeChallenge: string;
    token: string;
}

export const buildAuthCodeEndpoint = async ({ codeChallenge, token }: BuildAuthCodeEndpoint) => {
    const { clientId } = getAuthClient();
    const endpoint = await RouteService.getTokensRoute();
    return (
        endpoint +
        `authorization?
        token=${token}&
        response_type=code&
        client_id=${clientId}&
        code_challenge=${codeChallenge}&
        code_challenge_method=S256&
        redirect_uri=${window.location.origin}${import.meta.env.BASE_URL}account/confirm`.replace(/\s*\n*/g, "")
    );
};

interface InitSessionViaAuthCode {
    codeVerifier: string;
    code: string;
}

const initSessionViaAuthCode = async ({ code, codeVerifier }: InitSessionViaAuthCode): Promise<SessionToken> => {
    const { clientId, clientSecret } = getAuthClient();
    const bodyData = {
        grant_type: AuthGrantType.authorization_code,
        client_id: clientId,
        client_secret: clientSecret,
        code: code,
        code_verifier: codeVerifier,
        redirect_uri: `${window.location.origin}${window.location.pathname}`,
    };

    const token = await requestJwt({ body: bodyData });

    saveSession(token);
    return token;
};

const refreshSession = async (refreshToken = getSession()?.refresh_token): Promise<SessionToken> => {
    if (!refreshToken) {
        return Promise.reject();
    }
    const token = await requestRefreshJwt(refreshToken);
    saveSession(token);
    return token;
};

type LoginEndpointResponse = {
    model: { requiresTwoFactor: boolean } | null;
    message: string;
    details: string[];
};

const initiateLogin = async (email: string, password: string): Promise<LoginEndpointResponse> => {
    const endpoint = await RouteService.getAccountsRoute();
    const res = await fetch(endpoint + "login" + window.location.search, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Email: email, Password: password }),
    });
    if (!res.ok) {
        await throwHttpError(res);
    }
    return (await res.json()) as LoginEndpointResponse;
};

const submitTotpToLogin = async (totpCode: string): Promise<LoginEndpointResponse> => {
    const endpoint = await RouteService.getAccountsRoute();
    const res = await fetch(endpoint + "login" + window.location.search, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ TotpCode: totpCode }),
    });
    if (!res.ok) {
        await throwHttpError(res);
    }
    return (await res.json()) as LoginEndpointResponse;
};

// Login to get the cookie to access MVC controllers
// Will be removed when the application is completely migrated to React
const loadCookie = async (email: string, password: string, tft: string, totp: string): Promise<void> => {
    const configData = {
        headers: {
            "Content-Type": "application/json",
        },
        withCredentials: true,
    };

    const bodyData = {
        Email: email,
        Password: password,
        TotpToken: tft,
        TotpCode: totp,
    };
    const endpoint = await RouteService.getAccountsRoute();
    await HttpService.post(endpoint + "login", bodyData, configData);
};

const unloadCookie = async () => {
    const endpoint = await RouteService.getAccountsRoute();
    await HttpService.get(endpoint + "logout", { withCredentials: true });
};

const logout = async (): Promise<void> => {
    clearSession();
    await unloadCookie();
};

const getCurrentUser = (): JwtPayload | null => {
    try {
        const token = getSession();
        if (!token) {
            return null;
        }

        return jwtDecode<JwtPayload>(token.access_token);
    } catch (_error) {
        return null;
    }
};

const setIsUserTotpValid = (isValid: boolean): void => {
    LocalStorage.setItem("totp", isValid);
};
const isUserTotpValid = (): boolean => {
    return Boolean(LocalStorage.getItem<boolean>("totp"));
};

// Notices shown on the login form after a full page reload (see restartLoginFlow).
// This list is the single source of truth: the type is derived from it, and reads are
// validated against it so a stale value from an old deploy can never reach t().
const loginNoticeKeys = ["pages:loginPage.toasts.sessionExpired"] as const;
type LoginNoticeKey = (typeof loginNoticeKeys)[number];

const storeLoginNotice = (i18nKey: LoginNoticeKey): void => {
    sessionStorage.setItem(loginNoticeStorageKey, i18nKey);
};

const consumeLoginNotice = (): LoginNoticeKey | null => {
    const notice = sessionStorage.getItem(loginNoticeStorageKey);
    if (notice) {
        sessionStorage.removeItem(loginNoticeStorageKey);
    }
    return loginNoticeKeys.find((key) => key === notice) ?? null;
};

const restartLoginFlow = async (): Promise<void> => {
    clearSession();
    try {
        await unloadCookie();
    } catch {
        // redirect even if logout cookie call fails
    }
    window.location.href = toAppUrl("/login");
};

const isUserInRole = (neededRoles: AccessRole): boolean => {
    const user = getCurrentUser();
    if (!user || !user.role) {
        return false;
    }

    if (!Array.isArray(user.role)) {
        user.role = [user.role];
    }
    return user.role.some((role) => neededRoles.includes(role as EmteriaRoles));
};

const isUserInAnyAccessRole = (): boolean => {
    return isUserInRole(roles.accessRoles.AdminAllAccessRoles);
};

const isUserAdmin = (): boolean => {
    return isUserInRole([roles.emteriaRoles.EmteriaAdminRole]);
};

const isUserDeveloper = (): boolean => {
    return isUserInRole(roles.accessRoles.AdminDeveloperAccessRoles);
};

const isUserMerchant = (): boolean => {
    return isUserInRole(roles.accessRoles.AdminSalesAccessRoles);
};

const isUserAccountant = (): boolean => {
    return isUserInRole(roles.accessRoles.AdminAccountantAccessRoles);
};

const isUserSupporter = (): boolean => {
    return isUserInRole(roles.accessRoles.AdminSupportAccessRoles);
};

const mayUserSeeTenantSwitcher = (): boolean => {
    const env = getEnv();
    if (env === "production") {
        // sometimes we need to enable tenant switching in production for customer demos
        // in this case we can return isUserAdmin() here to only allow it for admins
        return false;
    } else {
        return true;
    }
};

const AuthService = {
    initiateAuthFlow,
    continueAuthFlowAfterLogin,
    initiateLogin,
    submitTotpToLogin,
    restartLoginFlow,
    storeLoginNotice,
    consumeLoginNotice,
    handleAuthCallback,
    resolveSafeReturnUrl,
    clearPendingAuth,
    initSessionViaAuthCode,
    saveSession,
    refreshSession,
    getSession,
    loadCookie,
    logout,
    getCurrentUser,
    isUserTotpValid,
    setIsUserTotpValid,
    isUserInRole,
    isUserSupporter,
    isUserMerchant,
    isUserAccountant,
    isUserDeveloper,
    isUserAdmin,
    isUserInAnyAccessRole,
    mayUserSeeTenantSwitcher,
    helpers: authHelpers,
};

export default AuthService;
